'use client';
import { jsx } from 'react/jsx-runtime';
import { useControllableState } from './hooks.mjs';
import { Children, cloneElement, useMemo, forwardRef, useRef, useId, useImperativeHandle, createContext, useContext } from 'react';

const clsx = (...classes)=>{
    const classNames = [];
    for (const cn of classes){
        if (!cn) continue;
        if (typeof cn === "string") {
            classNames.push(cn);
        }
        if (Array.isArray(cn)) {
            classNames.push(clsx(...cn));
        }
        if (typeof cn === "object") {
            for(const key in cn){
                if (cn[key]) {
                    classNames.push(key);
                }
            }
        }
    }
    return classNames.join(" ");
};

function Slot({ children, ...props }) {
    let SingleChild = null;
    try {
        SingleChild = Children.only(children);
    } catch (error) {
        if (process.env.NODE_ENV === "development") {
            console.error("Slot component must have exactly one child, got", children);
            throw error;
        }
    }
    if (!SingleChild) {
        console.warn("Slot component must have exactly one child");
        return null;
    }
    return /*#__PURE__*/ cloneElement(SingleChild, {
        ...props,
        className: clsx(SingleChild.props?.className, props?.className),
        style: {
            ...SingleChild.props?.style,
            ...props?.style
        }
    });
}

/**
 * Unique symbol used to reference the cummulitive variants,defaults & classNames of a Classed Component.
 */ const TW_VARS = Symbol.for("tw-classed.vars");

const mergeClass = (c1, c2)=>{
    if (typeof c2 === "boolean") return c1;
    if (!c2) return c1;
    // Check if c1 is empty
    if (!c1) return c2;
    return c1 + " " + c2;
};
/**
 * Parses & merging variants from a given string or variant config
 * @internal
 */ const parseClassNames = (classNames)=>{
    let stringClassNames = [];
    let variantObj = {};
    let defaultVariants = {};
    let compoundVariants = [];
    let dataAttributes = new Set();
    let defaultProps = {};
    for (const className of classNames){
        if (!className) continue;
        if (typeof className === "string") {
            stringClassNames.push(className);
            continue;
        }
        if (typeof className === "object" || typeof className === "function") {
            const record = Reflect.has(className, TW_VARS) ? Reflect.get(className, TW_VARS) : className;
            if (record.variants) Object.assign(variantObj, record.variants);
            if (record.defaultVariants) Object.assign(defaultVariants, record.defaultVariants);
            if (record.compoundVariants) record.compoundVariants.forEach((cv)=>compoundVariants.push(cv));
            if (record.className) stringClassNames.push(record.className);
            if (record.base) stringClassNames.push(record.base);
            if (record.dataAttributes) record.dataAttributes.forEach((name)=>{
                dataAttributes.add(name);
            });
            if (record.defaultProps) Object.assign(defaultProps, record.defaultProps);
        }
    }
    return {
        className: clsx(stringClassNames),
        variants: variantObj,
        defaultVariants,
        compoundVariants,
        dataAttributes: Array.from(dataAttributes),
        defaultProps
    };
};
/**
 * Gets the variant selector from the variant props
 */ const getVariantSelector = (variantKey, props, { defaultVariants })=>{
    const variantValue = props[variantKey];
    const vStringValue = variantValue?.toString();
    return vStringValue || defaultVariants?.[variantKey]?.toString();
};
const mapPropsToVariantClass = ({ variants, defaultVariants, compoundVariants }, props = {}, shouldDeleteProps = false)=>{
    const matchedKeys = new Set();
    const producedClassName = Object.keys(variants).reduce((acc, variantKey)=>{
        const variantSelector = getVariantSelector(variantKey, props, {
            defaultVariants
        });
        if (!variantSelector) return acc;
        shouldDeleteProps && matchedKeys.add(variantKey);
        const variantClassName = variants[variantKey]?.[variantSelector];
        if (!variantClassName) return acc;
        // Variant is matched
        return mergeClass(acc, variantClassName);
    }, "");
    const compoundedClassNames = getCompoundVariantClasses({
        props,
        defaultVariants
    }, compoundVariants);
    shouldDeleteProps && matchedKeys.forEach((key)=>delete props[key]);
    return mergeClass(producedClassName, compoundedClassNames?.join(" "));
};
function getCompoundVariantClasses({ props, defaultVariants }, compoundVariants = []) {
    return compoundVariants.reduce((acc, { class: cvClass, className: cvClassName, ...cvo })=>{
        const notMatched = Object.entries(cvo).some(([key, value])=>{
            const propValue = props[key];
            const valueToUse = propValue !== undefined ? propValue : defaultVariants?.[key];
            return Array.isArray(value) ? !value.includes(valueToUse) : valueToUse !== value;
        });
        if (!notMatched) {
            if (cvClass) acc.push(cvClass);
            if (cvClassName) acc.push(cvClassName);
        }
        return acc;
    }, []);
}
function getDataAttributes({ props, dataAttributes, variants, defaultVariants }) {
    if (dataAttributes.length === 0) return {};
    return dataAttributes.reduce((acc, name)=>{
        const variantName = props[name] ?? defaultVariants?.[name];
        const value = variants[name]?.[variantName];
        if (value != null) {
            acc[`data-${name}`] = variantName;
        }
        return acc;
    }, {});
}

const COMPONENT_SYMBOL = Symbol.for("tw-classed.component");
const isClassedComponent = (value)=>{
    return (typeof value === "object" || typeof value === "function") && Reflect.has(value, COMPONENT_SYMBOL);
};

const cx = (...args)=>args.filter((v)=>!!v && typeof v === "string").join(" ");
const internalClassed = (elementType, classNames, { merger = cx } = {})=>{
    const toParse = Array.from(classNames);
    const isClassed = isClassedComponent(elementType);
    if (isClassed) {
        toParse.unshift(elementType);
    }
    const { className, variants, defaultVariants, compoundVariants, dataAttributes, defaultProps } = parseClassNames(toParse);
    const Comp = ({ className: cName, asChild, ...props })=>{
        const Component = elementType;
        // Map props variant to className
        const [variantClassNames, dataAttributeProps] = useMemo(()=>{
            const dataAttributeProps = getDataAttributes({
                props,
                dataAttributes,
                variants,
                defaultVariants
            });
            return [
                mapPropsToVariantClass({
                    variants,
                    defaultVariants,
                    compoundVariants
                }, props, true),
                dataAttributeProps
            ];
        }, [
            props
        ]);
        const merged = useMemo(()=>merger(className, variantClassNames, cName), [
            className,
            cName,
            variantClassNames
        ]);
        const ComponentToUse = asChild ? Slot : Component;
        return /*#__PURE__*/ jsx(ComponentToUse, {
            className: merged,
            ...props,
            ...isClassed && Object.keys(defaultVariants).length ? defaultVariants : {},
            ...dataAttributeProps,
            ...defaultProps
        });
    };
    Comp.displayName = typeof elementType !== "string" ? elementType.displayName || elementType.name || "Compoonent" : `TwComponent(${elementType})`;
    Reflect.set(Comp, TW_VARS, {
        className,
        variants,
        defaultVariants,
        compoundVariants,
        dataAttributes
    });
    Reflect.set(Comp, COMPONENT_SYMBOL, true);
    return Comp;
};
const createClassed = (config)=>{
    const classedWithConfig = (elementType, ...args)=>{
        return internalClassed(elementType, args, config);
    };
    const classedProxy = new Proxy(classedWithConfig, {
        get: (_, type)=>{
            return function(...args) {
                return classedWithConfig.apply(this, [
                    type,
                    ...args
                ]);
            };
        }
    });
    return {
        classed: classedProxy
    };
};
createClassed();

const AccordionContext = /*#__PURE__*/ createContext(null);
const useAccordionContext = ()=>useContext(AccordionContext);
const Root = /*#__PURE__*/ forwardRef(({ className, type, color, children, value, onValueChange, ...rest }, ref)=>{
    const [state, setState] = useControllableState([], value, onValueChange);
    return /*#__PURE__*/ jsx(AccordionContext.Provider, {
        value: {
            type: type || "multiple",
            value: state,
            setValue: setState
        },
        children: /*#__PURE__*/ jsx("div", {
            ref: ref,
            className: clsx(className, "accordion-root"),
            ...rest,
            children: children
        })
    });
});
Root.displayName = "AccordionRoot";
const Item = /*#__PURE__*/ forwardRef(({ className, children, value: externalValue, ...rest }, ref)=>{
    const elementRef = useRef(null);
    const internalValue = useId();
    const value = externalValue || internalValue;
    const accordionContext = useAccordionContext();
    const accordions = accordionContext?.value ?? [];
    useImperativeHandle(ref, ()=>elementRef.current);
    const isOpen = accordions.includes(value);
    const handleToggle = (e)=>{
        if (e.currentTarget.open === isOpen) {
            return;
        }
        // Skip if toggled event is triggered by open prop changing
        const set = new Set([
            ...accordions,
            value
        ]);
        if (e.newState === "closed") {
            set.delete(value);
        } else {
            if (accordionContext?.type === "single") {
                set.clear();
            }
            set.add(value);
        }
        accordionContext?.setValue(Array.from(set));
    };
    return /*#__PURE__*/ jsx("details", {
        open: isOpen,
        "data-value": value,
        onToggle: handleToggle,
        ref: elementRef,
        className: clsx(className, "accordion-item"),
        ...rest,
        children: children
    });
});
Item.displayName = "AccordionItem";
const Trigger = /*#__PURE__*/ forwardRef(({ className, mobileMenu, children, ...rest }, ref)=>{
    return /*#__PURE__*/ jsx("summary", {
        ref: ref,
        className: clsx(className, "accordion-trigger"),
        ...rest,
        children: children
    });
});
Trigger.displayName = "AccordionTrigger";
const Content = /*#__PURE__*/ forwardRef(({ className, children, ...rest }, ref)=>{
    return /*#__PURE__*/ jsx("div", {
        ref: ref,
        className: clsx(className, "accordion-content"),
        ...rest,
        children: children
    });
});
Content.displayName = "AccordionContent";

var Accordion = {
  __proto__: null,
  Content: Content,
  Item: Item,
  Root: Root,
  Trigger: Trigger
};

export { Accordion as A, Content as C, Item as I, Root as R, Slot as S, Trigger as T, clsx as c };
